// 生成唯一步骤编码
export const generateUniqueStepCode = () => {
  const timestamp = new Date().getTime();
  const random = Math.floor(Math.random() * 10000); // 添加随机数部分
  return `STEP_${timestamp}_${random}`;
};

// 生成唯一模板编码
export const generateTemplateCode = (prefix = 'SOP') => {
  const timestamp = new Date().getTime();
  const random = Math.floor(Math.random() * 1000); // 添加随机数部分
  return `${prefix}_${timestamp}_${random}`;
};

// 获取消息类型显示名称
export const getMessageTypeName = (type, messageTypes) => {
  const messageType = messageTypes.find(item => item.value === type);
  return messageType ? messageType.label : type;
};

/**
 * 获取消息预览内容
 * @param {Object} message 消息对象
 * @returns {string} 预览内容
 */
export const getMessagePreview = (message) => {
  if (!message) return '';

  // 判断消息类型
  switch (message.type) {
    case 'TEXT':
      // 确保返回字符串而不是对象
      if (typeof message.params?.content === 'object') {
        return JSON.stringify(message.params.content);
      }
      return message.params?.content || '文本消息';
    case 'IMAGE':
      const imageUrl = message.params?.mediaId || '';
      if (imageUrl.startsWith('http')) {
        return <img src={imageUrl} alt="图片" style={{ maxWidth: '100px', maxHeight: '50px' }} />;
      }
      return '[图片]';
    case 'VIDEO':
      return '[视频]';
    case 'LINK':
      return `链接：${message.params?.title || ''}`;
    case 'MINIPROGRAM':
      return `小程序：${message.params?.title || ''}`;
    case 'FILE':
      return '[文件]';
    default:
      return '未知消息类型';
  }
};

/**
 * 获取朋友圈预览内容
 * @param {Object} moment 朋友圈内容对象
 * @returns {string} 预览内容
 */
export const getMomentPreview = (moment) => {
  if (!moment) return '';

  // 判断朋友圈内容类型
  switch (moment.type) {
    case 'MOMENT_TEXT':
      const textContent = moment.params?.content || '';
      return textContent.length > 30 ? `${textContent.substring(0, 30)}...` : textContent;
    case 'MOMENT_IMAGE':
      const imageUrl = moment.params?.mediaId || '';
      if (imageUrl.startsWith('http')) {
        // 返回纯文本而不是JSX
        return `[朋友圈图片] ${imageUrl.substring(0, 15)}...`;
      }
      return `[朋友圈图片] ${imageUrl.substring(0, 10)}...`;
    case 'MOMENT_VIDEO':
      const videoId = moment.params?.mediaId || '';
      return `[朋友圈视频] ${videoId.substring(0, 10)}...`;
    case 'MOMENT_LINK':
      const title = moment.params?.title || '';
      const url = moment.params?.url || '';
      const desc = moment.params?.description || '';
      // 返回纯文本而不是JSX
      return `[朋友圈链接] ${title} - ${desc.substring(0, 20)}${desc.length > 20 ? '...' : ''}`;
    default:
      return '未知朋友圈内容类型';
  }
};

// 获取TEXT类型消息的内容
export const getTextContent = (step) => {
  try {
    if (!step || !step.actionParams) {
      return '';
    }

    let actionParams = step.actionParams;

    // 如果actionParams是字符串，尝试解析
    if (typeof actionParams === 'string') {
      try {
        // 检查是否是转义的JSON字符串（例如: "{\"content\": \"122222\"}"）
        if (actionParams.includes('\\\"') || actionParams.includes('\\"')) {
          // 如果是转义的JSON字符串，先进行一次解析
          actionParams = JSON.parse(`"${actionParams.replace(/^"|"$/g, '')}"`);
          // 然后再次解析为对象
          actionParams = JSON.parse(actionParams);
        } else {
          // 普通JSON字符串直接解析
          actionParams = JSON.parse(actionParams);
        }
      } catch (e) {
        console.log('解析actionParams失败', e, actionParams);
        // 解析失败时,如果整个字符串就是content,则直接返回
        if (typeof actionParams === 'string' && !actionParams.includes('{') && !actionParams.includes('"content"')) {
          return actionParams;
        }
        return '';
      }
    }

    // 此时actionParams应该是对象
    return actionParams?.content || '';
  } catch (error) {
    console.error('getTextContent error:', error);
    return '';
  }
};

// 获取步骤的预览内容
export const getStepPreview = (step) => {
  if (!step) return '';

  // 如果有momentsList数据，使用第一个项目来显示
  if (step.type === 'MOMENTS' && step.momentsList && step.momentsList.length > 0) {
    const firstMoment = step.momentsList[0];
    if (firstMoment.type === 'MOMENT_TEXT') {
      return `朋友圈内容: ${firstMoment.params?.content || ''}`;
    } else if (firstMoment.type === 'MOMENT_IMAGE') {
      return '朋友圈图片内容';
    } else if (firstMoment.type === 'MOMENT_VIDEO') {
      return '朋友圈视频内容';
    } else if (firstMoment.type === 'MOMENT_LINK') {
      return `朋友圈链接: ${firstMoment.params?.title || ''}`;
    }
    return `朋友圈内容 (${step.momentsList.length}项)`;
  }

  // 根据动作类型返回不同的预览内容
  switch (step.actionType) {
    case 'TEXT':
      return `文本内容: ${getTextContent(step)}`;
    case 'MOMENTS':
    case 'SEND_MOMENT': // 添加对SEND_MOMENT类型的处理
      try {
        let params = step.actionParams;
        if (typeof params === 'string') {
          params = JSON.parse(params);
        }
        if (params.text && params.text.content) {
          return `朋友圈内容: ${params.text.content}`;
        }
        return '朋友圈内容';
      } catch (e) {
        return '朋友圈内容';
      }
    case 'IMAGE':
      return '图片消息';
    case 'VIDEO':
      return '视频消息';
    case 'LINK':
      return '链接消息';
    case 'MINIPROGRAM':
      return '小程序消息';
    // case 'FILE':
    //   return '文件消息';
    // case 'ADD_TAG':
    //   return '添加标签';
    // case 'REMOVE_TAG':
    //   return '移除标签';
    default:
      // 其他类型尝试从actionParams中获取描述信息
      if (step.actionParams) {
        try {
          const params = typeof step.actionParams === 'string'
            ? JSON.parse(step.actionParams)
            : step.actionParams;

          if (params.content) return `内容: ${params.content}`;
          if (params.description) return params.description;

          // 简单展示actionParams的摘要
          const paramsStr = JSON.stringify(params);
          return paramsStr.length > 50 ? `${paramsStr.substring(0, 50)}...` : paramsStr;
        } catch (e) {
          console.log('解析预览失败', e);
          return '';
        }
      }
      return ''; // 确保返回默认值
  }
};

// 获取指定天数和小时的步骤
export const getStepsByDayAndHour = (steps, day, hour) => {
  return steps.filter(step => {
    // 首先匹配天数
    if (step.timeOffsetDays !== day) return false;

    // 如果不需要匹配小时，或者步骤没有设置执行时间，则符合条件
    if (hour === null || !step.executeTime) return true;

    // 否则检查小时是否匹配
    if (step.executeTime) {
      // 从HH:mm:ss格式的时间字符串中提取小时
      const stepHour = parseInt(step.executeTime.split(':')[0], 10);
      return stepHour === hour;
    }

    return false;
  });
};

// 获取指定天数的所有执行时间
export const getHoursByDay = (steps, day) => {
  const daySteps = steps.filter(step => step.timeOffsetDays === day);
  // 从executeTime中提取小时
  return [...new Set(daySteps.map(step => {
    if (step.executeTime) {
      // 从HH:mm:ss格式的时间字符串中提取小时
      return parseInt(step.executeTime.split(':')[0], 10);
    }
    return null;
  }))].filter(hour => hour !== undefined && hour !== null).sort((a, b) => a - b);
};

// 获取实际日期
export const getActualDate = (offsetDays) => {
  const now = new Date();
  const targetDate = new Date(now);
  targetDate.setDate(now.getDate() + offsetDays);
  return `${targetDate.getMonth() + 1}月${targetDate.getDate()}日`;
}; 